import { AlertTriangle } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { useFetchCustomUserRoles } from '@app/modules/Admin/hooks/useCustomUserRoles'
import { USER_ROLES_LABELS_MAP } from '@app/app-constants'
import { DOCUMENT_SELECTABLE_ROLES } from '../../../documents.constants'
import { DocumentVisibility } from '../../../documents.types'

// A plain controlled value/onChange pair rather than a react-hook-form prop — see
// georeference-step.tsx for why.
type PermissionsStepProps = {
  visibility: DocumentVisibility
  roleIds: string[]
  onChange: (next: { visibility: DocumentVisibility; roleIds: string[] }) => void
  rolesError?: string
  // Edit only, failure case: GET /documents/:id/permissions prefills this step's roleIds
  // normally, but if that fetch fails there's no reliable current state to show — surface
  // that rather than silently rendering an empty (and misleadingly-saveable) checklist.
  existingRolesUnknown?: boolean
}

export const PermissionsStep = ({
  visibility,
  roleIds,
  onChange,
  rolesError,
  existingRolesUnknown,
}: PermissionsStepProps) => {
  const { data: customRoles } = useFetchCustomUserRoles()

  const roleOptions = [
    ...DOCUMENT_SELECTABLE_ROLES.map((role) => ({
      id: role as string,
      label: (USER_ROLES_LABELS_MAP as Record<string, string>)[role] ?? role,
    })),
    ...(customRoles ?? []).map((role) => ({
      id: role.name.toLowerCase(),
      label: role.description || role.name,
    })),
  ]

  const toggleRole = (roleId: string, checked: boolean) => {
    onChange({
      visibility: 'organisation',
      roleIds: checked ? [...roleIds, roleId] : roleIds.filter((id) => id !== roleId),
    })
  }

  const setPrivate = (checked: boolean) => {
    onChange(
      checked ? { visibility: 'personal', roleIds: [] } : { visibility: 'organisation', roleIds },
    )
  }

  const isPrivate = visibility === 'personal'

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-semibold">Permissions</h3>
        <p className="text-sm text-muted-foreground">
          Choose who can view and download this document.
        </p>
      </div>

      {existingRolesUnknown && !isPrivate && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <p>
            Couldn&apos;t load this document&apos;s current role assignments — only check roles
            below if you want to <strong>replace</strong> who has access. Leave this section alone
            to keep existing permissions unchanged.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 rounded-md border p-3 bg-slate-50">
        <Checkbox checked disabled id="admin-role" />
        <Label htmlFor="admin-role" className="font-normal">
          Admin <span className="text-muted-foreground">— always has access</span>
        </Label>
      </div>

      <div className="flex flex-col gap-2">
        {roleOptions.map((role) => (
          <div key={role.id} className="flex items-center gap-2 rounded-md border p-3">
            <Checkbox
              id={`role-${role.id}`}
              checked={!isPrivate && roleIds.includes(role.id)}
              disabled={isPrivate}
              onCheckedChange={(checked) => toggleRole(role.id, checked === true)}
            />
            <Label htmlFor={`role-${role.id}`} className="font-normal">
              {role.label}
            </Label>
          </div>
        ))}
      </div>

      {rolesError && <p className="text-sm text-destructive">{rolesError}</p>}

      <Separator />

      <div className="flex items-center gap-2">
        <Checkbox
          id="private-visibility"
          checked={isPrivate}
          onCheckedChange={(checked) => setPrivate(checked === true)}
        />
        <Label htmlFor="private-visibility" className="font-normal">
          Private — only you and Admins can view, download and edit this document.
        </Label>
      </div>

      <div className="rounded-md border bg-slate-50 p-3">
        <p className="text-xs font-medium text-muted-foreground mb-1.5">WHO CAN SEE THIS</p>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary" size="sm">
            Admin
          </Badge>
          {isPrivate
            ? null
            : roleIds.map((roleId) => (
                <Badge key={roleId} variant="secondary" size="sm">
                  {roleOptions.find((r) => r.id === roleId)?.label ?? roleId}
                </Badge>
              ))}
        </div>
      </div>
    </div>
  )
}
